import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, Shield, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/Authcontext';
import { useTheme } from '../../contexts/Themecontext';
import { authAPI } from '../../services/api';
import logo from '../../assets/img/logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Email/Password, 2: OTP
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(null);
  
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.requestLoginOTP({ email, password });
      
      if (response.requires_otp) {
        setStep(2);
        setOtpExpiry(response.otp_expiry_min || 10);
      }
    } catch (err) {
      console.error('OTP request error:', err);
      setError(
        err.response?.data?.error || 
        'Failed to send OTP. Please check your credentials.'
      );
      
      if (err.response?.status === 429) {
        const remaining = err.response?.data?.remaining;
        setError(`Too many attempts. Please try again in ${remaining} minutes.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Complete Login with OTP
  const handleCompleteLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({ email, password, otp });

      const { user, access_token, refresh_token, must_change_password } = response;

      if (!user) {
        throw new Error("Login successful but user data is missing");
      }
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Update auth context
      login(user, access_token);

      // Handle password change requirement
      if (must_change_password || user.must_change_password) {
        navigate('/change-password', { state: { forced: true } });
        return;
      }

      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      if (err.response?.status === 403 && err.response?.data?.must_change_password) {
        navigate('/change-password', { state: { forced: true } });
        return;
      }
      
      setError(
        err.response?.data?.error || 
        err.message || 
        'Login failed. Please try again.'
      );
      
      if (err.response?.status === 429) {
        const remaining = err.response?.data?.remaining;
        setError(`Too many attempts. Please try again in ${remaining} minutes.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp('');
    setStep(1);
    setError('');
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        card: 'bg-white',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        input: 'bg-white border-gray-300 text-gray-900 focus:border-blue-500',
        button: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500',
        accent: 'text-blue-600',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        card: 'bg-slate-800',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        input: 'bg-slate-900 border-slate-700 text-white focus:border-cyan-500',
        button: 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500',
        accent: 'text-cyan-400',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      card: 'bg-slate-900/50 backdrop-blur-xl',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      input: 'bg-slate-900/50 border-purple-500/30 text-white focus:border-purple-400',
      button: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500',
      accent: 'text-purple-400',
    };
  };

  const classes = getThemeClasses();

  return (
    <div className={`min-h-screen ${classes.bg} flex items-center justify-center px-4 py-12 pt-20 sm:px-6 lg:px-8`}>
      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center">
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-1 mb-2">
            <span className={`text-3xl font-bold ${classes.accent}`}>GLY</span>
            <span className={`text-3xl font-bold ${theme === 'quantum' ? 'text-pink-400' : classes.accent}`}>QIX</span>
          </div>
          <h2 className={`text-3xl font-bold ${classes.text}`}>
            {step === 1 ? 'Welcome Back' : 'Verify Your Identity'}
          </h2>
          <p className={`mt-2 ${classes.textMuted}`}>
            {step === 1 ? 'Sign in to your account' : 'Enter the OTP sent to your email'}
          </p>
        </div>

        {/* Main Card */}
        <div className={`${classes.card} border ${classes.border} rounded-2xl shadow-2xl p-8`}>
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Email & Password */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Mail className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`block w-full pl-10 pr-3 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2`}>Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <Lock className={`w-5 h-5 ${classes.textMuted}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`block w-full pl-10 pr-10 py-3 border ${classes.input} rounded-lg focus:outline-none transition-colors`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? 
                      <EyeOff className="w-5 h-5 text-gray-400" /> : 
                      <Eye className="w-5 h-5 text-gray-400" />
                    }
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <Shield size={20} />
                    Continue with OTP
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleCompleteLogin} className="space-y-6">
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${theme === 'quantum' ? 'bg-purple-500/20' : 'bg-blue-500/20'} mb-4`}>
                  <Shield size={32} className={classes.accent} />
                </div>
                <p className={`text-sm ${classes.textMuted}`}>
                  We've sent a 6-digit code to <strong>{email}</strong>
                </p>
                {otpExpiry && (
                  <p className={`text-xs ${classes.textMuted} mt-2`}>
                    Code expires in {otpExpiry} minutes
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${classes.text} mb-2 text-center`}>
                  Enter Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className={`block w-full px-3 py-4 border ${classes.input} rounded-lg focus:outline-none text-center tracking-widest text-2xl font-mono`}
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-semibold rounded-lg ${classes.button} shadow-lg transition-all disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    Sign In
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  className={`text-sm ${classes.accent} hover:opacity-80 font-medium transition-colors`}
                >
                  Didn't receive code? Try again
                </button>
              </div>
            </form>
          )}

          {/* Footer Links */}
          {step === 1 && (
            <div className="mt-6 text-center">
              <p className={`text-sm ${classes.textMuted}`}>
                Don't have an account?{' '}
                <Link 
                  to="/signup" 
                  className={`font-medium ${classes.accent} hover:opacity-80 transition-colors`}
                >
                  Sign up
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="text-center">
          <p className={`text-xs ${classes.textMuted} flex items-center justify-center gap-2`}>
            <Shield size={14} />
            Protected by OTP authentication
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;